import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Upload,
  Loader2,
  Download,
  Maximize2,
  X,
  AlertTriangle,
  Copy,
  Check,
  Archive,
} from 'lucide-react';
import { api, KinveraApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import {
  measureImageResolution,
  megapixelsOf,
  projectFactorOutput,
  evaluatePrintQuality,
  type ImageResolution,
} from '../services/resolution';
import { createZipBlob, dataUrlToUint8Array } from '../services/zipUtils';
import { GooglePhotosSaveButton } from '../components/GooglePhotosSaveButton';

type Caps = {
  enabled: boolean;
  factors: number[];
  maxOutputPixels?: number | null;
  creditCost: number;
  creditsRequired?: boolean;
  model?: string;
  provider?: string;
};

type ItemStatus = 'ready' | 'uploading' | 'upscaling' | 'complete' | 'failed';

type UpscaleItem = {
  localId: string;
  jobId?: string | null;
  operationId?: string | null;
  fileName: string;
  mimeType: string;
  base64Data: string;
  dataUrl: string;
  previewUrl: string;
  resolution: ImageResolution;
  factor: number;
  status: ItemStatus;
  resultUrl?: string | null;
  resultWidth?: number;
  resultHeight?: number;
  errorMessage?: string | null;
  errorCode?: string | null;
  errorDetail?: string | null;
  requestId?: string | null;
  diagnostics?: Record<string, unknown> | null;
};

const CONCURRENCY = 2;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 10 * 60 * 1000;

function fileToDataUrl(file: File): Promise<{ dataUrl: string; mimeType: string; base64Data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
      if (!match) {
        reject(new Error(`Could not read ${file.name}`));
        return;
      }
      resolve({ dataUrl, mimeType: match[1], base64Data: match[2] });
    };
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function formatDiag(item: UpscaleItem): string {
  const d = item.diagnostics || {};
  return [
    `Error code: ${item.errorCode || 'UPSCALE_FAILED'}`,
    `Message: ${item.errorMessage || ''}`,
    item.errorDetail ? `Detail: ${item.errorDetail}` : null,
    `Provider: ${d.provider || 'Leonardo.Ai'}`,
    `Model: ${d.model || ''}`,
    `Factor: ${item.factor}×`,
    `Input: ${item.resolution.width} × ${item.resolution.height}`,
    d.generationId ? `Generation ID: ${d.generationId}` : null,
    d.operationId || item.operationId
      ? `Operation ID: ${d.operationId || item.operationId}`
      : null,
    item.requestId ? `Request ID: ${item.requestId}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollUpscaleOperation(
  operationId: string,
  token: string | null | undefined,
  onTick?: (status: string) => void
) {
  const started = Date.now();
  while (Date.now() - started < POLL_MAX_MS) {
    const op = await api.upscaleOperation(operationId, token);
    onTick?.(op.status);
    if (op.status === 'completed') return op;
    if (op.status === 'failed') {
      throw new KinveraApiError(op.error?.message || 'Upscale failed. Your photo is unchanged.', {
        code: op.error?.code || 'UPSCALE_FAILED',
        diagnostics: op.diagnostics || null,
      });
    }
    await sleep(POLL_INTERVAL_MS);
  }
  throw new KinveraApiError('Upscale is taking longer than expected. Please try again shortly.', {
    code: 'UPSCALE_TIMEOUT',
    retryable: true,
  });
}

async function mapPool<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export const UpscalePage: React.FC = () => {
  const { token, productConfig } = useAuth();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get('jobId');
  const fileRef = useRef<HTMLInputElement>(null);

  const [caps, setCaps] = useState<Caps>({
    enabled: true,
    factors: [2, 3, 4],
    maxOutputPixels: null,
    creditCost: 1,
    provider: 'leonardo',
  });
  const [items, setItems] = useState<UpscaleItem[]>([]);
  const [bulkFactor, setBulkFactor] = useState(2);
  const [dragging, setDragging] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const freeAlpha =
    productConfig?.creditMode === 'disabled' ||
    productConfig?.creditMode === 'metered' ||
    productConfig?.stage === 'alpha' ||
    !productConfig?.creditsEnforced;

  useEffect(() => {
    api
      .upscaleCapabilities()
      .then((c) => {
        const factors = c.factors?.length ? c.factors : [2, 3, 4];
        setCaps({
          enabled: c.enabled !== false,
          factors,
          maxOutputPixels:
            c.maxOutputPixels == null ? null : Number(c.maxOutputPixels) || null,
          creditCost: Number(c.creditCost ?? 1),
          creditsRequired: Boolean(c.creditsRequired),
          model: c.model,
          provider: c.provider || 'leonardo',
        });
        setBulkFactor(factors[0] || 2);
      })
      .catch(() => {
        /* keep defaults — page remains usable */
      });
  }, []);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      setPageError(null);
      const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (!list.length) {
        setPageError('Please choose JPEG, PNG, or WebP images.');
        return;
      }
      const next: UpscaleItem[] = [];
      for (const file of list) {
        try {
          const parsed = await fileToDataUrl(file);
          const resolution = await measureImageResolution(parsed.dataUrl);
          const defaultFactor =
            caps.factors.find((f) =>
              projectFactorOutput(resolution.width, resolution.height, f, caps.maxOutputPixels)
                .withinLimit
            ) || caps.factors[0] || 2;
          next.push({
            localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            fileName: file.name,
            mimeType: parsed.mimeType,
            base64Data: parsed.base64Data,
            dataUrl: parsed.dataUrl,
            previewUrl: parsed.dataUrl,
            resolution,
            factor: defaultFactor,
            status: 'ready',
          });
        } catch (err) {
          setPageError(err instanceof Error ? err.message : 'Failed to read an image');
        }
      }
      if (next.length) setItems((prev) => [...prev, ...next]);
    },
    [caps.factors, caps.maxOutputPixels]
  );

  // Load Library job into workspace
  useEffect(() => {
    if (!jobIdParam || !token) return;
    let cancelled = false;
    (async () => {
      setLoadingJob(true);
      setPageError(null);
      try {
        const src = await api.upscaleSource(jobIdParam, token);
        if (cancelled) return;
        const resolution = {
          width: src.width,
          height: src.height,
          megapixels: megapixelsOf(src.width, src.height),
          aspectRatio: Number((src.width / src.height).toFixed(4)),
        };
        const defaultFactor =
          caps.factors.find((f) =>
            projectFactorOutput(resolution.width, resolution.height, f, caps.maxOutputPixels)
              .withinLimit
          ) ||
          caps.factors[0] ||
          2;
        setItems([
          {
            localId: `job-${src.jobId}`,
            jobId: src.jobId,
            fileName: src.fileName,
            mimeType: src.mimeType,
            base64Data: src.base64Data,
            dataUrl: src.dataUrl,
            previewUrl: src.dataUrl,
            resolution,
            factor: defaultFactor,
            status: 'ready',
          },
        ]);
      } catch (err) {
        if (!cancelled) {
          setPageError(
            err instanceof KinveraApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Could not load Library photo for upscale'
          );
        }
      } finally {
        if (!cancelled) setLoadingJob(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobIdParam, token, caps.factors, caps.maxOutputPixels]);

  const updateItem = (localId: string, patch: Partial<UpscaleItem>) => {
    setItems((prev) => prev.map((it) => (it.localId === localId ? { ...it, ...patch } : it)));
  };

  const removeItem = (localId: string) => {
    setItems((prev) => prev.filter((it) => it.localId !== localId));
  };

  const runOne = async (item: UpscaleItem) => {
    updateItem(item.localId, {
      status: 'uploading',
      errorMessage: null,
      errorCode: null,
      errorDetail: null,
      diagnostics: null,
      operationId: null,
    });
    try {
      const payload = {
        image: {
          mimeType: item.mimeType,
          base64Data: item.base64Data,
          fileName: item.fileName,
        },
        factor: item.factor,
        inputWidth: item.resolution.width,
        inputHeight: item.resolution.height,
        fileName: item.fileName,
      };

      const started = item.jobId
        ? await api.upscaleJob(item.jobId, payload, token)
        : await api.upscale(payload, token);

      updateItem(item.localId, {
        status: 'upscaling',
        jobId: started.jobId || item.jobId,
        operationId: started.operationId,
        diagnostics: (started.diagnostics as Record<string, unknown>) || {
          provider: 'Leonardo.Ai',
          model: caps.model,
          factor: item.factor,
          inputWidth: item.resolution.width,
          inputHeight: item.resolution.height,
          operationId: started.operationId,
        },
      });

      const op = await pollUpscaleOperation(started.operationId, token, (status) => {
        if (status === 'processing') {
          updateItem(item.localId, { status: 'upscaling' });
        }
      });

      const resultUrl = op.imageUrl;
      if (!resultUrl) {
        throw new KinveraApiError('Upscale completed but no image was returned.', {
          code: 'LEONARDO_RESULT_MISSING',
          diagnostics: op.diagnostics || null,
        });
      }

      let resultWidth = Number(op.outputWidth) || item.resolution.width * item.factor;
      let resultHeight = Number(op.outputHeight) || item.resolution.height * item.factor;
      try {
        const measured = await measureImageResolution(resultUrl);
        resultWidth = measured.width;
        resultHeight = measured.height;
      } catch {
        /* keep reported dims */
      }

      updateItem(item.localId, {
        status: 'complete',
        jobId: op.jobId || started.jobId || item.jobId,
        operationId: op.id || started.operationId,
        resultUrl,
        resultWidth,
        resultHeight,
        diagnostics: (op.diagnostics as Record<string, unknown>) || null,
      });
    } catch (err) {
      const apiErr = err instanceof KinveraApiError ? err : null;
      updateItem(item.localId, {
        status: 'failed',
        errorMessage: apiErr?.message || (err instanceof Error ? err.message : 'Upscale failed'),
        errorCode: apiErr?.code || 'UPSCALE_FAILED',
        errorDetail: apiErr?.detail || null,
        requestId: apiErr?.requestId || null,
        diagnostics: (apiErr?.diagnostics as Record<string, unknown>) || {
          provider: 'Leonardo.Ai',
          model: caps.model,
          factor: item.factor,
          inputWidth: item.resolution.width,
          inputHeight: item.resolution.height,
        },
      });
    }
  };

  const runSelected = async (targets: UpscaleItem[]) => {
    const ready = targets.filter((t) => t.status === 'ready' || t.status === 'failed');
    if (!ready.length) return;
    await mapPool(ready, CONCURRENCY, async (item) => {
      await runOne(item);
    });
  };

  const applyBulkFactor = (factor: number) => {
    setBulkFactor(factor);
    setItems((prev) =>
      prev.map((it) => {
        const proj = projectFactorOutput(
          it.resolution.width,
          it.resolution.height,
          factor,
          caps.maxOutputPixels
        );
        if (
          !proj.withinLimit ||
          it.status === 'uploading' ||
          it.status === 'upscaling' ||
          it.status === 'complete'
        ) {
          return it;
        }
        return { ...it, factor, status: 'ready', errorMessage: null };
      })
    );
  };

  const completed = items.filter((i) => i.status === 'complete' && i.resultUrl);
  const readyCount = items.filter((i) => i.status === 'ready' || i.status === 'failed').length;
  const busy = items.some((i) => i.status === 'upscaling' || i.status === 'uploading');

  const downloadAll = async () => {
    if (!completed.length) return;
    setZipping(true);
    try {
      if (completed.length === 1) {
        const it = completed[0];
        const a = document.createElement('a');
        a.href = it.resultUrl!;
        const base = it.fileName.replace(/\.[^/.]+$/, '') || 'photo';
        a.download = `kinvera-upscaled-${base}-${it.factor}x.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      const files = completed.map((it) => {
        const base = it.fileName.replace(/\.[^/.]+$/, '') || 'photo';
        return {
          name: `kinvera-upscaled-${base}-${it.factor}x.png`,
          data: dataUrlToUint8Array(it.resultUrl!),
        };
      });
      const blob = createZipBlob(files);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kinvera-upscaled-batch.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setZipping(false);
    }
  };

  const copyDetails = async (item: UpscaleItem) => {
    try {
      await navigator.clipboard.writeText(formatDiag(item));
      setCopiedId(item.localId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <main className="kin-page kin-upscale-page">
      <div className="kin-page-head">
        <div>
          <h1>Prepare for Print</h1>
          <p className="kin-section-lede" style={{ marginTop: '0.35rem' }}>
            Upload a photo and choose a print size. Kinvera recommends the smallest enhancement needed
            for a high-quality print — no restoration required.
          </p>
          {freeAlpha ? (
            <p className="kin-upscale-free-note">Free during Kinvera Alpha</p>
          ) : null}
        </div>
        <div className="kin-upscale-head-actions">
          <Link to="/app/library" className="kin-btn kin-btn-ghost">
            Library
          </Link>
          <button
            type="button"
            className="kin-btn kin-btn-primary"
            onClick={() => fileRef.current?.click()}
            disabled={!caps.enabled}
          >
            <Upload size={16} aria-hidden /> Upload photos
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              if (e.target.files) void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {!caps.enabled ? (
        <p className="kin-banner kin-banner-error">
          Print preparation is temporarily unavailable. Restorations and Library still work.
        </p>
      ) : null}
      {pageError ? <p className="kin-banner kin-banner-error">{pageError}</p> : null}
      {loadingJob ? (
        <p className="kin-upscale-loading">
          <Loader2 className="animate-spin" size={16} /> Loading Library photo…
        </p>
      ) : null}

      {items.length === 0 && !loadingJob ? (
        <div
          className={`kin-upscale-dropzone${dragging ? ' is-dragging' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files?.length) void addFiles(e.dataTransfer.files);
          }}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click();
          }}
        >
          <Maximize2 size={28} aria-hidden />
          <strong>Drop photos to prepare for print</strong>
          <span>JPEG, PNG, or WebP · no restoration required</span>
        </div>
      ) : null}

      {items.length > 0 ? (
        <>
          <div className="kin-upscale-bulk-bar">
            <span>
              {items.length} photo{items.length === 1 ? '' : 's'} ready
            </span>
            <label className="kin-upscale-bulk-factor">
              Advanced factor
              <select
                value={bulkFactor}
                onChange={(e) => applyBulkFactor(Number(e.target.value))}
                disabled={busy}
              >
                {caps.factors.map((f) => (
                  <option key={f} value={f}>
                    {f}×
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="kin-btn kin-btn-primary"
              disabled={busy || readyCount === 0 || !caps.enabled}
              onClick={() => void runSelected(items)}
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Preparing…
                </>
              ) : (
                `Prepare ${readyCount > 1 ? 'all' : ''}`
              )}
            </button>
            {completed.length > 0 ? (
              <button
                type="button"
                className="kin-btn kin-btn-ghost"
                disabled={zipping}
                onClick={() => void downloadAll()}
              >
                <Archive size={16} aria-hidden />{' '}
                {zipping ? 'Preparing…' : completed.length > 1 ? 'Download all' : 'Download'}
              </button>
            ) : null}
          </div>

          <ul className="kin-upscale-grid">
            {items.map((item) => {
              const proj = projectFactorOutput(
                item.resolution.width,
                item.resolution.height,
                item.factor,
                caps.maxOutputPixels
              );
              const print =
                item.status === 'complete' && item.resultWidth && item.resultHeight
                  ? evaluatePrintQuality(item.resultWidth, item.resultHeight)
                  : evaluatePrintQuality(item.resolution.width, item.resolution.height);

              return (
                <li key={item.localId} className="kin-upscale-card">
                  <div className="kin-upscale-card-media">
                    <img
                      src={item.resultUrl || item.previewUrl}
                      alt={item.fileName}
                      loading="lazy"
                    />
                    <button
                      type="button"
                      className="kin-upscale-card-remove"
                      aria-label={`Remove ${item.fileName}`}
                      disabled={item.status === 'upscaling' || item.status === 'uploading'}
                      onClick={() => removeItem(item.localId)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="kin-upscale-card-body">
                    <strong className="kin-upscale-card-title" title={item.fileName}>
                      {item.fileName}
                    </strong>
                    <div className="kin-upscale-meta">
                      Current: {item.resolution.width.toLocaleString()} ×{' '}
                      {item.resolution.height.toLocaleString()} · {item.resolution.megapixels} MP
                    </div>

                    <div className="kin-upscale-factors" role="group" aria-label="Print size">
                      {print.sizes.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          disabled={
                            item.status === 'uploading' ||
                            item.status === 'upscaling' ||
                            item.status === 'complete' ||
                            !s.canPrepare
                          }
                          className={`kin-upscale-factor-btn${
                            s.recommendedFactor === item.factor ||
                            (!s.upscaleRequired && item.factor === caps.factors[0])
                              ? ' is-active'
                              : ''
                          }`}
                          onClick={() => {
                            const nextFactor = s.upscaleRequired
                              ? s.recommendedFactor || item.factor
                              : caps.factors[0] || 2;
                            updateItem(item.localId, {
                              factor: nextFactor,
                              status: 'ready',
                              errorMessage: s.canPrepare ? null : s.message,
                            });
                          }}
                          title={s.message}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <details className="kin-upscale-tech">
                      <summary>Advanced factor</summary>
                      <div className="kin-upscale-factors" role="group" aria-label="Upscale factor">
                        {caps.factors.map((f) => {
                          const p = projectFactorOutput(
                            item.resolution.width,
                            item.resolution.height,
                            f,
                            caps.maxOutputPixels
                          );
                          return (
                            <button
                              key={f}
                              type="button"
                              disabled={
                                !p.withinLimit ||
                                item.status === 'uploading' ||
                                item.status === 'upscaling' ||
                                item.status === 'complete'
                              }
                              className={`kin-upscale-factor-btn${item.factor === f ? ' is-active' : ''}`}
                              onClick={() =>
                                updateItem(item.localId, {
                                  factor: f,
                                  status: 'ready',
                                  errorMessage: null,
                                })
                              }
                              title={
                                p.withinLimit
                                  ? `${p.outputWidth}×${p.outputHeight}`
                                  : 'Exceeds provider limit'
                              }
                            >
                              {f}×
                            </button>
                          );
                        })}
                      </div>
                    </details>

                    <div className="kin-upscale-meta">
                      {proj.withinLimit ? (
                        <>
                          Selected {item.factor}× → {proj.outputWidth.toLocaleString()} ×{' '}
                          {proj.outputHeight.toLocaleString()} · {proj.megapixels} MP
                        </>
                      ) : (
                        <span className="kin-upscale-warn">
                          {item.factor}× unavailable — exceeds provider limit
                        </span>
                      )}
                    </div>

                    <div className="kin-upscale-status">
                      {item.status === 'ready' && 'Ready'}
                      {item.status === 'uploading' && (
                        <span className="kin-upscale-status-busy">
                          <Loader2 size={14} className="animate-spin" /> Uploading…
                        </span>
                      )}
                      {item.status === 'upscaling' && (
                        <span className="kin-upscale-status-busy">
                          <Loader2 size={14} className="animate-spin" /> Preparing…
                        </span>
                      )}
                      {item.status === 'complete' && 'Complete'}
                      {item.status === 'failed' && (
                        <span className="kin-upscale-status-fail">
                          <AlertTriangle size={14} /> Failed
                        </span>
                      )}
                    </div>

                    {item.status === 'complete' && item.resultWidth && item.resultHeight ? (
                      <div className="kin-upscale-before-after">
                        <div>
                          <span>Before</span>
                          <strong>
                            {item.resolution.width} × {item.resolution.height}
                          </strong>
                          <em>{item.resolution.megapixels} MP</em>
                        </div>
                        <div>
                          <span>After</span>
                          <strong>
                            {item.resultWidth} × {item.resultHeight}
                          </strong>
                          <em>{megapixelsOf(item.resultWidth, item.resultHeight)} MP</em>
                        </div>
                      </div>
                    ) : null}

                    {item.status === 'complete' ? (
                      <div className="kin-upscale-print">
                        {print.sizes.slice(0, 4).map((s) => (
                          <span key={s.label}>
                            {s.label}: {s.rating}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {item.status === 'failed' ? (
                      <div className="kin-upscale-error">
                        <p>
                          <strong>Upscale failed.</strong>
                        </p>
                        <p>Code: {item.errorCode}</p>
                        {item.errorDetail ? (
                          <p className="kin-upscale-error-detail">
                            Detail: {item.errorDetail}
                          </p>
                        ) : (
                          <p>{item.errorMessage}</p>
                        )}
                        <details className="kin-upscale-tech">
                          <summary>Technical details</summary>
                          <pre>{formatDiag(item)}</pre>
                          <button
                            type="button"
                            className="kin-btn kin-btn-ghost"
                            onClick={() => void copyDetails(item)}
                          >
                            {copiedId === item.localId ? (
                              <>
                                <Check size={14} /> Copied
                              </>
                            ) : (
                              <>
                                <Copy size={14} /> Copy details
                              </>
                            )}
                          </button>
                        </details>
                        <button
                          type="button"
                          className="kin-btn kin-btn-ghost"
                          onClick={() =>
                            updateItem(item.localId, {
                              status: 'ready',
                              errorMessage: null,
                            })
                          }
                        >
                          Try again
                        </button>
                      </div>
                    ) : null}

                    <div className="kin-upscale-card-actions">
                      {item.status === 'ready' || item.status === 'failed' ? (
                        <button
                          type="button"
                          className="kin-btn kin-btn-primary"
                          disabled={busy || !proj.withinLimit || !caps.enabled}
                          onClick={() => void runOne(item)}
                        >
                          Upscale {item.factor}×
                        </button>
                      ) : null}
                      {item.status === 'complete' && item.resultUrl ? (
                        <>
                          <a
                            className="kin-btn kin-btn-primary"
                            href={item.resultUrl}
                            download={`kinvera-upscaled-${item.fileName.replace(/\.[^/.]+$/, '')}-${item.factor}x.png`}
                          >
                            <Download size={16} /> Download Upscaled
                          </a>
                          <GooglePhotosSaveButton
                            variant="toolbar"
                            imageUrl={item.resultUrl}
                            fileName={`kinvera-upscaled-${item.fileName}`}
                            watermarkExportsEnabled={
                              productConfig?.watermarkExportsEnabled !== false
                            }
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </main>
  );
};
